import { useEffect, useState } from "react";
import Button from "@mui/material/Button/Button";
import styles from "./RtsScanModule.styles";
import PackagesList from "./PackagesList";
import AddReasonCodeDialog from "./AddReasonCodeDialog";
import { DedicatedDeliveryAgent } from "../../app/api/dedicatedDelivery/DedicatedDeliveryAgent";
import RtsScanStore from "./RtsScanStore";
import { v4 as uuidv4 } from "uuid";
import LocationComponent from "./LocationComponent";
import {
  RtsScannedDataReq,
  RtsScannedDataResponse,
  ScanSource,
} from "../../app/models/requestModels/rtsRequest";
import { format, utcToZonedTime } from "date-fns-tz";
import { AddRtsAgent } from "../../app/api/rts/AddRtsAgent";
import { toast } from "react-toastify";
import ManualEntryComponent from "./ManualEntryComponent";
import ResponseDialog from "./ResponseDailog";
import Loader from "../../components/common/loader/Loader";
import { useSelector } from "react-redux";
import { RootState } from "../../app/features/store/store";
import DriverIDSelection from "../driverIdSelection/DriverIDSelection";
import { useSearchParams } from "react-router-dom";
import VideoCapture from "../../components/common/VideoCapture/VideoCapture";
import RtsClientSelection from "./modals/RtsClientSelection";
import { useNavigate } from "react-router-dom";

const RtsScanModule = () => {
  const {
    scannedData,
    setEventTypes,
    latestScanBarcode,
    setLatestScanBarcode,
    resetScannedData,
    setLatestImage,
    latestImage,
    assocClientId,
    rtsUserClient,
    setAssocClientId,
  } = RtsScanStore();
  const navigate = useNavigate();
  const { location } = RtsScanStore();

  const [loading, setLoading] = useState<boolean>(false);

  const isFdp = useSelector(
    (state: RootState) => state.login?.authResponse?.isFdp
  );
  const { globallySelectedDriverClientEmail } = useSelector(
    (state: RootState) => state.fdp
  );
  const [openReasonModal, setOpenReasonModal] = useState<boolean>(false);
  const [openScanModule, setOpenScanModule] = useState<boolean>(true);
  const [openManualEntryModule, setOpenManualEntryModule] =
    useState<boolean>(false);
  const [scanSource, setScanSource] = useState<ScanSource>(
    ScanSource.BarcodeScan
  );
  const [openResponseDialog, setOpenResponseDialog] = useState<boolean>(false);
  const [successResponses, setSuccessResponses] =
    useState<RtsScannedDataResponse[]>();
  const [failedResponses, setFailedResponses] =
    useState<RtsScannedDataResponse[]>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [torch, setTorch] = useState<boolean>(false);
  const [showClientSelection, setShowClientSelection] = useState(false);
  const [showDriverEmailSelection, setShowDriverEmailSelection] =
    useState(false);

  useEffect(() => {
    if (!rtsUserClient && location?.isFenced && isFdp) {
      setShowClientSelection(true);
      setShowDriverEmailSelection(false);
    }
    if (
      rtsUserClient &&
      location?.isFenced &&
      !globallySelectedDriverClientEmail &&
      isFdp
    ) {
      setShowDriverEmailSelection(true);
      setShowClientSelection(false);
    }
    if (rtsUserClient) {
      setAssocClientId(rtsUserClient.id);
    }
  }, [rtsUserClient, location?.isFenced]);

  useEffect(() => {
    const fetchReasonCodesByClient = async (clientId: number) => {
      await DedicatedDeliveryAgent.getRtsEventTypes(clientId).then((resp) => {
        if (resp && resp.isSuccess) {
          setEventTypes(resp.data);
        }
      });
    };

    if (assocClientId) {
      setLoading(true);
      fetchReasonCodesByClient(assocClientId);
      setLoading(false);
    }
  }, [assocClientId]);

  function handleScanBarcode(barcode: string) {
    setLatestScanBarcode(barcode);
  }

  function handleReasonModalClose() {
    setOpenReasonModal(false);
    setLatestScanBarcode("");
    setLatestImage("");
    setOpenScanModule(true);
  }

  function handleOnCloseManualEntryComponent() {
    setLatestScanBarcode("");
    setLatestImage("");
    setOpenManualEntryModule(false);
    setOpenScanModule(true);
  }

  function handleOpenReasonModalAndPauseScanModule() {
    setOpenReasonModal(true);
    setOpenScanModule(false);
  }

  function handleSubmitManualEntryComponent() {
    setOpenReasonModal(true);
    if (latestImage != "") {
      setScanSource(ScanSource.ImageUpload);
    } else {
      setScanSource(ScanSource.Manual);
    }
    setOpenScanModule(false);
    setOpenManualEntryModule(false);
  }

  function handleOpenManualEntryModule() {
    setOpenScanModule(false);
    setOpenManualEntryModule(true);
  }

  function handleOnCloseResponseDialog() {
    setOpenResponseDialog(false);
    setSuccessResponses(undefined);
    setFailedResponses(undefined);
    navigate("/");
  }

  function handleOnCloseDriverSelectionDialog() {
    setShowDriverEmailSelection(false);
  }

  async function uploadImagesToCloudBucket(
    scannedData: RtsScannedDataReq[]
  ): Promise<RtsScannedDataReq[]> {
    for (const scanData of scannedData) {
      if (scanData.imageUrl) {
        var files: string[] = [scanData.imageUrl];
        try {
          const response = await AddRtsAgent.uploadImage({ files: files });
          if (response?.imageStatus[0]?.accessUrl) {
            scanData.imageUrl = response?.imageStatus[0]?.accessUrl;
          } else {
            scanData.imageUrl = undefined;
            toast.error("Error in uploading image file.");
          }
        } catch (error) {
          scanData.imageUrl = undefined;
          toast.error("Error in uploading image file.");
        }
      }
    }

    return scannedData.filter(
      (scanData) =>
        !(
          scanData.imageUrl === undefined &&
          scanData.scanSource === "image_upload"
        )
    );
  }

  const submitRtsData = async () => {
    setOpenScanModule(false);
    setLoading(true);
    uploadImagesToCloudBucket(scannedData).then(async (resp) => {
      const rtsData = {
        latitude: location.latitude,
        longitude: location.longitude,
        storeName: location.storeId,
        scannedData: resp,
        selectedEmail: globallySelectedDriverClientEmail?.driverEmail as string,
      };
      try {
        if (scannedData.length !== 0) {
          const response = await AddRtsAgent.addRts(rtsData);
          if (response?.data?.rtsData) {
            var rtsResponse: RtsScannedDataResponse[] = response.data.rtsData;
            const successResponses = rtsResponse.filter(
              (item) => item.success === true
            );
            const failedResponses = rtsResponse.filter(
              (item) => item.success === false
            );
            if (successResponses.length > 0) {
              console.log("Success Responses : ", successResponses);
              setSuccessResponses(successResponses);
            }
            if (failedResponses.length > 0) {
              console.log("Failed Responses : ", failedResponses);
              setFailedResponses(failedResponses);
            }
          }
        }
      } catch (error: any) {
        toast.error(error);
      }
      setLoading(false);
      setOpenResponseDialog(true);
    });
    resetScannedData();
  };

  function toggleTorch(): void {
    setTorch(!torch);
  }

  return (
    <>
      {loading && <Loader isLoading={loading} />}
      {showClientSelection && (
        <RtsClientSelection setShowClientSelection={setShowClientSelection} />
      )}
      {showDriverEmailSelection && (
        <DriverIDSelection
          newSelection={true}
          setShowDriverSelection={handleOnCloseDriverSelectionDialog}
          setShowClientSelection={setShowClientSelection}
          routeCheckout={searchParams.get("routeCheckout")}
        />
      )}
      <LocationComponent />
      {openScanModule &&
        location.isFenced &&
        assocClientId &&
        globallySelectedDriverClientEmail &&
        rtsUserClient && (
          <>
            {
              // <BarcodeScannerComponent
              //   webcamRef={webcamRef}
              //   width="100%"
              //   height="35%"
              //   facingMode="environment"
              //   torch={torch}
              //   onUpdate={(err, result) => {
              //     if (result) {
              //       setLatestScanBarcode(result.getText());
              //       setOpenReasonModal(true);
              //       setOpenScanModule(false);
              //     } else {
              //       console.log("Barcode not found", new Date());
              //       caches.keys().then(function (names) {
              //         for (let name of names) caches.delete(name);
              //       });
              //     }
              //   }}
              // />
              <VideoCapture
                openReasonModal={handleOpenReasonModalAndPauseScanModule}
                setLatestScanBarcode={handleScanBarcode}
              />
            }

            <PackagesList />

            <div style={styles.scanModuleButtons}>
              <Button
                style={styles.buttonItem}
                variant="outlined"
                onClick={() => handleOpenManualEntryModule()}
              >
                Enter manually
              </Button>

              <Button
                disabled={scannedData.length == 0}
                style={styles.buttonItem}
                onClick={submitRtsData}
                variant="contained"
              >
                Submit
              </Button>
            </div>
          </>
        )}
      {openResponseDialog && (
        <ResponseDialog
          open={openResponseDialog}
          onClose={handleOnCloseResponseDialog}
          successResponses={successResponses}
          failedResponses={failedResponses}
        />
      )}
      {openManualEntryModule && (
        <ManualEntryComponent
          onClose={handleOnCloseManualEntryComponent}
          onSubmit={handleSubmitManualEntryComponent}
        />
      )}

      {openReasonModal && (
        <AddReasonCodeDialog
          open={openReasonModal}
          onClose={handleReasonModalClose}
          scanData={{
            uuid: uuidv4(),
            barcode: latestScanBarcode,
            imageUrl: latestImage,
            scanSource: scanSource,
            eventTypeId: 0,
            scannedTimestamp: format(
              utcToZonedTime(new Date(), "UTC"),
              "yyyy-MM-dd HH:mm:ss"
            ),
            scannedLocalTime: format(new Date(), "yyyy-MM-dd HH:mm:ss"),
          }}
        />
      )}
    </>
  );
};

export default RtsScanModule;
